import { ApiError } from './api.ts';

/** A channel is followed through its handle or one of its stream links (channel ids stay private). */
export type FollowTarget = { handle: string } | { streamId: string };

export interface FollowState {
  followers: number;
  following: boolean;
  notify: boolean;
  self: boolean;
  /** You blocked this creator: no Follow button. */
  blocked: boolean;
  followId: string | null;
}

export interface NotifySettings {
  /** Signed in to an account (not a guest): email/text can only use the account's own address. */
  account: boolean;
  push: { available: boolean; vapidPublicKey: string | null; browsers: string[] };
  email: { available: boolean; address: string | null; on: boolean };
  sms: { available: boolean; number: string | null; on: boolean };
}

export interface MyFollow {
  id: string;
  notify: boolean;
  createdAt: number;
  displayName: string | null;
  handle: string | null;
  liveId: string | null;
}

async function call<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(`/api/v1${path}`, { credentials: 'include', ...init, headers: { 'Content-Type': 'application/json', ...init.headers } });
  const body = await res.json().catch(() => null);
  if (!res.ok) {
    const err = (body as { error?: { code: string; message: string } } | null)?.error;
    throw new ApiError(res.status, err?.code ?? 'error', err?.message ?? `Request failed (${res.status})`);
  }
  return body as T;
}

const query = (t: FollowTarget) => ('handle' in t ? `handle=${encodeURIComponent(t.handle)}` : `streamId=${encodeURIComponent(t.streamId)}`);

export const followApi = {
  state: (t: FollowTarget) => call<FollowState>(`/follows?${query(t)}`),
  follow: (t: FollowTarget) => call<FollowState>('/follows', { method: 'POST', body: JSON.stringify(t) }),
  unfollow: (t: FollowTarget) => call<FollowState>('/follows', { method: 'DELETE', body: JSON.stringify(t) }),
  mine: () => call<{ follows: MyFollow[] }>('/me/follows'),
  setNotify: (followId: string, notify: boolean) => call(`/me/follows/${encodeURIComponent(followId)}`, { method: 'PATCH', body: JSON.stringify({ notify }) }),
  remove: (followId: string) => call(`/me/follows/${encodeURIComponent(followId)}`, { method: 'DELETE' }),
  settings: () => call<NotifySettings>('/me/notify'),
  setMethods: (m: { email?: boolean; sms?: boolean }) => call('/me/notify', { method: 'PATCH', body: JSON.stringify(m) }),
  unsubscribeInfo: (token: string) => call<UnsubInfo>(`/notify/unsubscribe?t=${encodeURIComponent(token)}`),
  unsubscribe: (token: string, undo = false) => call<UnsubInfo>(`/notify/unsubscribe?t=${encodeURIComponent(token)}${undo ? '&undo=1' : ''}`, { method: 'POST' }),
};

export interface UnsubInfo {
  kind: 'channel' | 'email' | 'sms';
  channel: string | null;
  /** The alert is on (before unsubscribing, or after Undo). */
  on: boolean;
}

// ── This browser (web push) ─────────────────────────────────────────────────────────────

/**
 * Can this browser get alerts? iPhone and iPad only deliver web push to sites added to the
 * Home Screen (iOS/iPadOS 16.4+), so there the answer is "add to Home Screen first".
 */
export type PushSupport = 'supported' | 'ios-home-screen' | 'unsupported';

export function pushSupport(): PushSupport {
  const ios = /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  const standalone = matchMedia('(display-mode: standalone)').matches || (navigator as Navigator & { standalone?: boolean }).standalone === true;
  if (ios && !standalone) return 'ios-home-screen';
  if (!('serviceWorker' in navigator) || !('PushManager' in window) || !('Notification' in window)) return 'unsupported';
  return 'supported';
}

function keyBytes(b64u: string): Uint8Array<ArrayBuffer> {
  const b64 = b64u.replace(/-/g, '+').replace(/_/g, '/') + '='.repeat((4 - (b64u.length % 4)) % 4);
  return Uint8Array.from(atob(b64), (c) => c.charCodeAt(0));
}

async function sha256Hex(s: string) {
  const d = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(s));
  return [...new Uint8Array(d)].map((b) => b.toString(16).padStart(2, '0')).join('');
}

/** Register the service worker ahead of the tap, so the tap can go straight to the permission prompt. */
export function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!('serviceWorker' in navigator)) return Promise.resolve(null);
  return navigator.serviceWorker.register('/sw.js').catch(() => null);
}

/** Is this browser one of the account's (or guest's) subscribed browsers? */
export async function pushOnHere(settings: NotifySettings): Promise<boolean> {
  if (pushSupport() !== 'supported' || Notification.permission !== 'granted') return false;
  const reg = await navigator.serviceWorker.getRegistration('/');
  const sub = await reg?.pushManager.getSubscription();
  return Boolean(sub && settings.push.browsers.includes(await sha256Hex(sub.endpoint)));
}

/** Ask for permission (call from the tap itself), subscribe, and tell the API about this browser. */
export async function enablePush(vapidPublicKey: string): Promise<void> {
  // First thing, while the tap still counts as a user gesture (Safari requires it).
  const permission = Notification.permission === 'granted' ? 'granted' : await Notification.requestPermission();
  if (permission !== 'granted') {
    throw new Error(permission === 'denied' ? 'Notifications are blocked for this site. Allow them in your browser’s site settings, then try again.' : 'Allow notifications to get alerts in this browser.');
  }
  const reg = (await registerServiceWorker()) ?? (await navigator.serviceWorker.ready);
  let sub = await reg.pushManager.getSubscription();
  const key = keyBytes(vapidPublicKey);
  // Subscribed under another server key (keys were changed): start over.
  const current = sub?.options.applicationServerKey ? new Uint8Array(sub.options.applicationServerKey) : null;
  if (sub && (!current || current.length !== key.length || current.some((b, i) => b !== key[i]))) {
    await sub.unsubscribe();
    sub = null;
  }
  try {
    sub ??= await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: key });
  } catch {
    throw new Error('This browser couldn’t turn on alerts. Private windows often can’t; try a normal one.');
  }
  await call('/me/push', { method: 'POST', body: JSON.stringify(sub.toJSON()) });
}

export async function disablePush(): Promise<void> {
  const reg = await navigator.serviceWorker.getRegistration('/');
  const sub = await reg?.pushManager.getSubscription();
  if (!sub) return;
  await call('/me/push', { method: 'DELETE', body: JSON.stringify({ endpoint: sub.endpoint }) }).catch(() => undefined);
  await sub.unsubscribe().catch(() => undefined);
}
